import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth, API } from "@/App";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import { ArrowLeft, Building2, User, UserPlus, Save, AlertTriangle, Info, Upload, X, Image, Globe } from "lucide-react";
import { ModuleHeaderIcon } from "@/components/ModuleHeader";
import PhoneInput from "@/components/PhoneInput";
import CountrySelect from "@/components/CountrySelect";
import { isValidEmail } from "@/utils/countries";
import UnsavedChangesGuard from "@/components/UnsavedChangesGuard";


const SOURCE_OPTIONS = [
  "Recomendación Cliente",
  "Recomendación Interna",
  "Página WEB",
  "Redes Sociales",
  "Publicidad Digital",
  "Publicidad Impresa",
  "Radio-TV"
];

const NewClient = () => {
  const { token } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [showRfcDialog, setShowRfcDialog] = useState(false);
  const [showDuplicateAlert, setShowDuplicateAlert] = useState(false);
  const [showConfirmDuplicate, setShowConfirmDuplicate] = useState(false);
  const [duplicateClient, setDuplicateClient] = useState(null);
  const [similarClients, setSimilarClients] = useState([]);
  const [showSimilarAlert, setShowSimilarAlert] = useState(false);
  const [showConfirmSimilar, setShowConfirmSimilar] = useState(false);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);

  const initialFormData = {
    company_name: "",
    nombre_comercial: "",
    rfc: "",
    country: "",
    address: "",
    contact_name: "",
    phone: "",
    email: "",
    source: "",
    logo_url: "",
    bind_client_id: "",
    dias_credito: 0
  };

  const [formData, setFormData] = useState(initialFormData);
  const [uploadingLogo, setUploadingLogo] = useState(false);

  // Detectar cambios en el formulario
  useEffect(() => {
    const hasChanges = formData.company_name !== "" ||
      formData.rfc !== "" ||
      formData.contact_name !== "" ||
      formData.phone !== "" ||
      formData.email !== "";
    setHasUnsavedChanges(hasChanges);
  }, [formData]);

  const updateField = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleDiscardChanges = () => {
    setFormData(initialFormData);
    setHasUnsavedChanges(false);
  };

  const handleLogoUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith('image/')) {
      toast.error("Solo se permiten archivos de imagen");
      return;
    }

    // Validate file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      toast.error("El archivo es muy grande. Máximo 5MB.");
      return;
    }

    setUploadingLogo(true);
    try {
      const formDataUpload = new FormData();
      formDataUpload.append('file', file);

      const res = await axios.post(`${API}/upload/logo`, formDataUpload, {
        headers: { 
          Authorization: `Bearer ${token}`,
          'Content-Type': 'multipart/form-data'
        }
      });

      updateField("logo_url", res.data.url);
      toast.success("Logo subido correctamente");
    } catch (error) {
      console.error("Error uploading logo:", error);
      toast.error("Error al subir el logo");
    } finally {
      setUploadingLogo(false);
    }
  };

  const removeLogo = () => {
    updateField("logo_url", "");
  };

  const handleRfcBlur = () => {
    if (formData.rfc === "") {
      setShowRfcDialog(true);
    }
  };

  const selectGenericRfc = (type) => {
    if (type === "national") {
      updateField("rfc", "XAXX010101000");
    } else {
      updateField("rfc", "XEXX010101000");
    }
    setShowRfcDialog(false);
  };

  const checkDuplicates = async () => {
    try {
      const res = await axios.get(`${API}/clients/check-duplicate`, {
        params: { rfc: formData.rfc, company_name: formData.company_name },
        headers: { Authorization: `Bearer ${token}` }
      });

      if (res.data.rfc_duplicate) {
        setDuplicateClient(res.data.rfc_duplicate_client);
        setShowDuplicateAlert(true);
        return false;
      }

      if (res.data.similar_names && res.data.similar_names.length > 0) {
        setSimilarClients(res.data.similar_names);
        setShowSimilarAlert(true);
        return "similar";
      }

      return true;
    } catch (error) {
      console.error("Error checking duplicates:", error);
      return true;
    }
  };

  const handleSubmit = async (forceSave = false, forceDuplicate = false) => {
    // Validate required fields
    if (!formData.company_name || !formData.rfc || !formData.contact_name || !formData.phone || !formData.email) {
      toast.error("Por favor completa todos los campos obligatorios");
      return;
    }

    // Validate email format
    if (!isValidEmail(formData.email)) {
      toast.error("Por favor ingresa un email válido (debe contener @)");
      return;
    }

    if (!forceSave && !forceDuplicate) {
      const duplicateCheck = await checkDuplicates();
      if (duplicateCheck === false) return;
      if (duplicateCheck === "similar") return;
    }

    setLoading(true);
    try {
      const payload = { ...formData };
      if (forceDuplicate) {
        payload.force_duplicate = true;
      }
      
      
      const response = await axios.post(`${API}/clients`, payload, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      const newClient = response.data;
      setHasUnsavedChanges(false);
      toast.success("Cliente registrado correctamente");
      
      // Navigate to the new client's detail page
      navigate(`/clientes/${newClient.id}`);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Error al registrar cliente");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="p-4 md:p-6 lg:p-8" data-testid="new-client-page">
      <UnsavedChangesGuard 
        hasUnsavedChanges={hasUnsavedChanges}
        onSave={() => handleSubmit(false)}
        onDiscard={handleDiscardChanges}
      />
      <div className="max-w-3xl mx-auto">
        {/* Header */}
        <div className="flex items-center gap-4 mb-6">
          <Button variant="ghost" size="icon" onClick={() => navigate("/clientes/crm")}>
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <ModuleHeaderIcon icon={UserPlus} />
          <div>
            <h1 className="text-2xl font-bold text-slate-900">Nuevo Cliente</h1>
            <p className="text-slate-500 text-sm">Registra un nuevo cliente en el sistema</p>
          </div>
        </div>

        {/* Form */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Building2 className="w-5 h-5" />
              Datos del Cliente
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-6">
            {/* Company Info */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="company_name">Empresa / Razón Social *</Label>
                <Input
                  id="company_name"
                  value={formData.company_name}
                  onChange={(e) => updateField("company_name", e.target.value.toUpperCase())}
                  style={{ textTransform: 'uppercase' }}
                  placeholder="Nombre de la empresa"
                  data-testid="company-name-input"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="nombre_comercial">Nombre comercial del cliente</Label>
                <Input
                  id="nombre_comercial"
                  value={formData.nombre_comercial}
                  onChange={(e) => updateField("nombre_comercial", e.target.value)}
                  placeholder="Cómo se mostrará en documentos al cliente final"
                  data-testid="nombre-comercial-input"
                />
                <p className="text-xs text-slate-400">Si se deja vacío, se usa la razón social.</p>
              </div>
              <div className="space-y-2">
                <Label htmlFor="rfc">RFC *</Label>
                <Input
                  id="rfc"
                  value={formData.rfc}
                  onChange={(e) => updateField("rfc", e.target.value.toUpperCase())}
                  onBlur={handleRfcBlur}
                  placeholder="RFC de la empresa"
                  maxLength={13}
                  data-testid="rfc-input"
                />
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="country">País de Origen</Label>
                <CountrySelect
                  value={formData.country}
                  onChange={(v) => updateField("country", v)}
                  placeholder="Seleccionar país"
                  className="w-full"
                  data-testid="country-select"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="address">Dirección</Label>
                <Input
                  id="address"
                  value={formData.address}
                  onChange={(e) => updateField("address", e.target.value)}
                  placeholder="Dirección completa"
                  data-testid="address-input"
                />
              </div>
            </div>

            {/* Logo del Cliente */}
            <div className="space-y-2">
              <Label htmlFor="logo" className="flex items-center gap-2">
                <Image className="w-4 h-4" />
                Logo del Cliente
              </Label>
              <p className="text-sm text-slate-500 mb-2">
                Este logo aparecerá junto al nombre del cliente en las cotizaciones
              </p>
              {formData.logo_url ? (
                <div className="flex items-center gap-4">
                  <div className="relative">
                    <img 
                      src={formData.logo_url.startsWith('http') ? formData.logo_url : `${API.replace('/api', '')}${formData.logo_url}`}
                      alt="Logo del cliente" 
                      className="h-16 w-auto max-w-[150px] object-contain border rounded-lg p-2 bg-white"
                    />
                    <button
                      type="button"
                      onClick={removeLogo}
                      className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-1 hover:bg-red-600 transition-colors"
                    >
                      <X className="w-3 h-3" />
                    </button>
                  </div>
                  <span className="text-sm text-green-600">Logo cargado</span>
                </div>
              ) : (
                <div className="flex items-center gap-2">
                  <label className="cursor-pointer">
                    <input
                      type="file"
                      accept="image/*"
                      onChange={handleLogoUpload}
                      className="hidden"
                      data-testid="logo-upload-input"
                    />
                    <div className="flex items-center gap-2 px-4 py-2 border-2 border-dashed border-slate-300 rounded-lg hover:border-blue-500 hover:bg-blue-50 transition-colors">
                      <Upload className="w-4 h-4 text-slate-500" />
                      <span className="text-sm text-slate-600">
                        {uploadingLogo ? "Subiendo..." : "Subir logo"}
                      </span>
                    </div>
                  </label>
                  <span className="text-xs text-slate-400">PNG, JPG hasta 5MB</span>
                </div>
              )}
            </div>

            {/* Contact Info */}
            <div className="border-t pt-6">
              <h3 className="font-semibold mb-4 flex items-center gap-2">
                <User className="w-4 h-4" />
                Datos de Contacto
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="contact_name">Nombre del Contacto *</Label>
                  <Input
                    id="contact_name"
                    value={formData.contact_name}
                    onChange={(e) => updateField("contact_name", e.target.value)}
                    placeholder="Nombre completo"
                    data-testid="contact-name-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="phone">Teléfono *</Label>
                  <PhoneInput
                    value={formData.phone}
                    onChange={(v) => updateField("phone", v)}
                    data-testid="phone-input"
                  />
                </div>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
                <div className="space-y-2">
                  <Label htmlFor="email">Email *</Label>
                  <Input
                    id="email"
                    type="email"
                    value={formData.email}
                    onChange={(e) => updateField("email", e.target.value)}
                    placeholder="correo@empresa.com"
                    data-testid="email-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="source">¿Cómo te enteraste de nosotros?</Label>
                  <Select value={formData.source} onValueChange={(v) => updateField("source", v)}>
                    <SelectTrigger data-testid="source-select">
                      <SelectValue placeholder="Seleccionar opción" />
                    </SelectTrigger>
                    <SelectContent>
                      {SOURCE_OPTIONS.map(option => (
                        <SelectItem key={option} value={option}>{option}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </div>

            {/* Vinculación Bind ERP */}
            <div className="border-t pt-6">
              <h3 className="font-semibold mb-4 flex items-center gap-2">
                <Globe className="w-4 h-4" />
                Vinculación Bind ERP
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="bind_client_id">No. Cliente en Bind</Label>
                  <Input
                    id="bind_client_id"
                    value={formData.bind_client_id}
                    onChange={(e) => updateField("bind_client_id", e.target.value)}
                    placeholder="Ej: CLI-00123"
                    data-testid="bind-client-id-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="dias_credito">Días de crédito</Label>
                  <Input
                    id="dias_credito"
                    type="number"
                    min={0}
                    value={formData.dias_credito ?? 0}
                    onChange={(e) => updateField("dias_credito", e.target.value === "" ? 0 : parseInt(e.target.value, 10) || 0)}
                    placeholder="0"
                    data-testid="dias-credito-input"
                  />
                  <p className="text-xs text-slate-500">Se usa como default al registrar CFDI del cliente.</p>
                </div>
                <div className="space-y-2">
                  <Label>Estatus ERP</Label>
                  <div className="flex items-center gap-2 h-10">
                    {formData.bind_client_id ? (
                      <Badge className="bg-green-100 text-green-700 border border-green-300">Vinculado</Badge>
                    ) : (
                      <Badge variant="secondary" className="bg-slate-100 text-slate-500">Desvinculado</Badge>
                    )}
                  </div>
                </div>
              </div>
            </div>

            {/* Actions */}
            <div className="flex justify-end gap-4 pt-6 border-t">
              <Button variant="outline" onClick={() => navigate("/clientes/crm")}>
                Cancelar
              </Button>
              <Button onClick={() => handleSubmit(false)} disabled={loading} data-testid="save-client-btn">
                <Save className="w-4 h-4 mr-2" />
                {loading ? "Guardando..." : "Guardar Cliente"}
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* RFC Dialog */}
        <Dialog open={showRfcDialog} onOpenChange={setShowRfcDialog}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Info className="w-5 h-5 text-blue-500" />
                RFC Genérico
              </DialogTitle>
              <DialogDescription>
                El campo RFC está vacío. Puedes usar un RFC genérico:
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-3 py-4">
              <Button
                variant="outline"
                className="w-full justify-start"
                onClick={() => selectGenericRfc("national")}
              >
                <span className="font-mono mr-2">XAXX010101000</span>
                <span className="text-slate-500">- Público General Nacional</span>
              </Button>
              <Button
                variant="outline"
                className="w-full justify-start"
                onClick={() => selectGenericRfc("foreign")}
              >
                <span className="font-mono mr-2">XEXX010101000</span>
                <span className="text-slate-500">- Público General Extranjero</span>
              </Button>
            </div>
            <DialogFooter>
              <Button variant="ghost" onClick={() => setShowRfcDialog(false)}>
                Ingresar RFC manualmente
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Duplicate RFC Alert */}
        <Dialog open={showDuplicateAlert} onOpenChange={setShowDuplicateAlert}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2 text-red-600">
                <AlertTriangle className="w-5 h-5" />
                RFC Duplicado
              </DialogTitle>
              <DialogDescription>
                Ya existe un cliente registrado con este RFC:
              </DialogDescription>
            </DialogHeader>
            {duplicateClient && (
              <div className="bg-red-50 p-4 rounded-lg space-y-2">
                <p>
                  <strong>Folio:</strong>{" "}
                  <a 
                    href={`/clientes/${duplicateClient.id}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-blue-600 hover:underline font-mono"
                  >
                    {duplicateClient.folio}
                  </a>
                </p>
                <p><strong>Empresa:</strong> {duplicateClient.company_name}</p>
                <p><strong>RFC:</strong> {duplicateClient.rfc}</p>
                <p><strong>Contacto:</strong> {duplicateClient.contact_name}</p>
                <p><strong>Email:</strong> {duplicateClient.email}</p>
              </div>
            )}
            <DialogFooter className="gap-2">
              <Button variant="outline" onClick={() => setShowDuplicateAlert(false)}>
                Cerrar
              </Button>
              <Button 
                variant="destructive" 
                onClick={() => {
                  setShowDuplicateAlert(false);
                  setShowConfirmDuplicate(true);
                }}
              >
                Registrar Duplicado
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Confirm Duplicate Registration */}
        <Dialog open={showConfirmDuplicate} onOpenChange={setShowConfirmDuplicate}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2 text-amber-600">
                <AlertTriangle className="w-5 h-5" />
                Confirmar Registro Duplicado
              </DialogTitle>
              <DialogDescription>
                ¿Estás seguro de registrar un cliente con un RFC que ya existe en el sistema?
              </DialogDescription>
            </DialogHeader>
            <div className="py-4 text-center text-slate-600">
              Esta acción creará un registro duplicado que podría causar confusión en el sistema.
            </div>
            <DialogFooter className="gap-2">
              <Button variant="outline" onClick={() => setShowConfirmDuplicate(false)}>
                Cancelar
              </Button>
              <Button 
                variant="destructive"
                onClick={() => {
                  setShowConfirmDuplicate(false);
                  handleSubmit(true, true);
                }}
              >
                Registrar
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Similar Names Alert */}
        <Dialog open={showSimilarAlert} onOpenChange={setShowSimilarAlert}>
          <DialogContent className="max-w-2xl">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2 text-amber-600">
                <AlertTriangle className="w-5 h-5" />
                Clientes Similares Encontrados
              </DialogTitle>
              <DialogDescription>
                Se encontraron clientes con nombres similares:
              </DialogDescription>
            </DialogHeader>
            <div className="max-h-60 overflow-y-auto space-y-2">
              {similarClients.map(client => (
                <div key={client.id} className="bg-amber-50 p-3 rounded-lg text-sm">
                  <p>
                    <strong>Folio:</strong>{" "}
                    <a 
                      href={`/clientes/${client.id}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-blue-600 hover:underline font-mono"
                    >
                      {client.folio}
                    </a>
                  </p>
                  <p><strong>Empresa:</strong> {client.company_name}</p>
                  <p><strong>RFC:</strong> {client.rfc}</p>
                  <p><strong>Contacto:</strong> {client.contact_name} | {client.email}</p>
                </div>
              ))}
            </div>
            <DialogFooter className="gap-2">
              <Button variant="outline" onClick={() => setShowSimilarAlert(false)}>
                Cancelar
              </Button>
              <Button 
                variant="destructive"
                onClick={() => {
                  setShowSimilarAlert(false);
                  setShowConfirmSimilar(true);
                }}
              >
                Registrar de Todas Formas
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Confirm Similar Registration */}
        <Dialog open={showConfirmSimilar} onOpenChange={setShowConfirmSimilar}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2 text-amber-600">
                <AlertTriangle className="w-5 h-5" />
                Confirmar Registro
              </DialogTitle>
              <DialogDescription>
                ¿Estás seguro de registrar un cliente con un nombre similar a uno existente?
              </DialogDescription>
            </DialogHeader>
            <div className="py-4 text-center text-slate-600">
              Esta acción podría causar confusiones en el sistema al tener clientes con nombres parecidos.
            </div>
            <DialogFooter className="gap-2">
              <Button variant="outline" onClick={() => setShowConfirmSimilar(false)}>
                Cancelar
              </Button>
              <Button 
                onClick={() => {
                  setShowConfirmSimilar(false);
                  handleSubmit(true, false);
                }}
              >
                Registrar
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </div>
  );
};

export default NewClient;
